import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";

import { Container, DialogContent } from "@mui/material";
import Menu from "@mui/material/Menu";
import dayjs from "dayjs";
import Tooltip from "@mui/material/Tooltip";

import { useJwtStorage, useLocalStorage } from "../../hooks/useLocalStorage";
import InputLabel from "@mui/material/InputLabel";
import Alert from "@mui/material/Alert";
import Link from "@mui/material/Link";
import Button from "@mui/material/Button";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";

import Paper from "@mui/material/Paper";
import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import ButtonGroup from "@mui/material/ButtonGroup";
import TableRowNoResults from "../../components/TableRowNoResults";

import { doService } from "../../services/ServiceHelper";

import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";

import DrawerAppBar from "../../components/DrawerAppBar";
import Title from "../../components/Title";
import SubTitle from "../../components/SubTitle";

import "./TeacherCalendar.css";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin, { Draggable } from "@fullcalendar/interaction";
import timeGridPlugin from "@fullcalendar/timegrid";

import IconButton from "@mui/material/IconButton";
import { ReactComponent as DeleteIcon } from "../../icons/Delete24.svg";

import { ReactComponent as VisibilityIcon } from "../../icons/Visibility24.svg";
import { ReactComponent as GradingIcon } from "../../icons/Grading24.svg";

import {
  getPeriodName,
  getPeriodSingleName,
} from "../../services/SchoolService";
import { getUserPriority } from "../../services/UserService";
import {
  getTeacherCalendar,
  getTeacherActivitiesDetailed,
  getTeacherActivities,
  saveTeacherDay,
  validateTeacherDay,
  changeActivityDateLocation,
  saveActivityColor,
  deleteActivityDates,
} from "../../services/ActivityService";
import { getPreviousAttendance } from "../../services/ReportService";

import clone from "lodash-es/clone";
import truncate from "lodash-es/truncate";
import remove from "lodash-es/remove";
import isEmpty from "lodash-es/isEmpty";

import { isStringEmpty } from "../../services/StringService";
import { isMobile } from "react-device-detect";
import { showError, showToast, showInfo } from "../../components/ATPDialogs";
import VideoAlert from "../../components/VideoAlert";
import { getYYYYMMDD__JsDate } from "../../services/DateService";
import {
  StripedTableCell,
  StripedTableRow,
} from "../../components/StripedTable";
import { getLocations } from "../../services/SchoolService";
import { ColorSelectDialog } from "../../components/ColorSelectDialog";
import ProgressBar from "../../components/ProgressBar";
import { adminBulkAddActivityDates } from "../../services/AdminService";

const TeacherCalendar = () => {
  const [_school] = useLocalStorage("Auth", "school", null);
  const [_jwt] = useJwtStorage();
  const [_showProgress, setShowProgress] = useState(false);

  const [_calendarEvents, setCalendarEvents] = useState([]);
  const [_calendarMeta, setCalendarMeta] = useState({ teacher: {} });

  const [_backDropOpen, setBackDropOpen] = useState(false);

  const [_roDayDialogOpen, setRoDayDialogOpen] = useState(false);
  const [_editDayDialogOpen, setEditDayDialogOpen] = useState(false);
  const [_warningDialogOpen, setWarningDialogOpen] = useState(false);
  const [_warningMessage, setWarningMessage] = useState("");
  const [_attendeeDialogOpen, setAttendeeDialogOpen] = useState(false);

  const [_calendarRatio, setCalendarRatio] = useState(0.86);
  const [_disabledDaysMap, setDisabledDaysMap] = useState({});
  const [_activityDateMap, setActivityDateMap] = useState({});

  const [_dialogTitleDetail, setDialogTitleDetail] = useState("");
  const [_selectedPeriod, setSelectedPeriod] = useState(1);
  const [_selectedDragPeriod, setSelectedDragPeriod] = useState(1);

  const [_selectedActivityEid, setSelectedActivityEid] = useState("XX");

  const [_activities, setActivities] = useState([]);

  const COLORS = [
    "#1f2937",
    "#2563eb",
    "#9333ea",
    "#312e81",
    "#FF7F50",
    "#1e3a8a",
    "#9f1239",
    "#0f766e",
  ];

  const [_selectedActivityMap, setSelectedActivityMap] = useState({
    p1: [],
    p2: [],
    p3: [],
    p4: [],
    p5: [],
    p6: [],
    p7: [],
    p8: [],
    p9: [],
    p10: [],
    p11: [],
    p12: [],
    p13: [],
    p14: [],
    p15: [],
    p16: [],
    p17: [],
    p18: [],
    p19: [],
    p20: [],
  });

  // based on period selected, assigned one of the arrays identified in the map above.
  const [_selectedActivityList, setSelectedActivityList] = useState([]);
  const [_selectedActivityDateStr, setSelectedActivityDateStr] = useState("");

  const calendarRef = useRef(null);
  const draggableActivitiesRef = useRef(null);
  const navigate = useNavigate();
  const { otherTeacherEid } = useParams();

  const [_activityAttendees, setActivityAttendees] = useState([]);
  const [_activityDates, setActivityDates] = useState([]);
  const [_locations, setLocations] = useState([]);
  const [_locationEid, setLocationEid] = useState({});
  const [_locationDialogOpen, setLocationDialogOpen] = useState(false);
  const [_selectedActivityDate, setSelectedActivityDate] = useState({});
  const [_colorSelectDialogOpen, setColorSelectDialogOpen] = useState(false);
  const [_color] = useState(COLORS[0]);
  const [_selectedColorActivity, setSelectedColorActivity] = useState("XX");
  const [_calendarContextMenu, setCalendarContextMenu] = useState(null);
  const [_calendarContextActivity, setCalendarContextActivity] = useState(null);
  useEffect(() => {
    setBackDropOpen(true);
    initTeacherCalendar();
    initTeacherActivities();
    initLocations();
  }, []);

  useEffect(() => {
    if (isMobile === true || draggableActivitiesRef.current == null) {
      return;
    }

    const draggable = new Draggable(draggableActivitiesRef.current, {
      itemSelector: ".teacher-calendar-draggable-activity",
      eventData: (eventEl) => ({
        title: eventEl.getAttribute("data-activity-name"),
        activityEid: eventEl.getAttribute("data-activity-eid"),
        create: false,
      }),
    });

    return () => {
      draggable.destroy();
    };
  }, [_activities]);

  const initLocations = () => {
    doService(getLocations(_school, _jwt), (result) => {
      setLocations(result.data);
    });
  };

  const initTeacherActivities = () => {
    if (!isEmpty(otherTeacherEid)) {
      // if admin then load for another teacher
      doService(
        getTeacherActivitiesDetailed(_school, _jwt, otherTeacherEid),
        (results) => {
          setActivities(results.data.activities);
        },
      );
    } else {
      // load self
      doService(getTeacherActivities(_school, _jwt), (results) => {
        setActivities(results.data);
      });
    }
  };

  const initTeacherCalendar = () => {
    initCalendarData();

    if (isMobile) {
      setCalendarRatio(0.81);
    } else {
      setCalendarRatio(1.66);
    }
  };

  const initCalendarData = () => {
    const currentDate = dayjs();
    const startDate = currentDate.subtract(21, "day");
    const endDate = currentDate.add(300, "day");

    doService(
      getTeacherCalendar(
        _school,
        _jwt,
        startDate.format("YYYY-MM-DD"),
        endDate.format("YYYY-MM-DD"),
        otherTeacherEid,
      ),
      (results) => {
        const activityDates = results.data.dates;
        setActivityDates(activityDates);
        const meta = results.data.meta;
        setCalendarMeta(meta);

        const disabledDaysMap = {};
        for (let date of meta.excludedDates) {
          disabledDaysMap[date.date] = date;
        }
        setDisabledDaysMap(disabledDaysMap);

        // init array
        const activityDateMap = {};
        for (let ad of activityDates) {
          activityDateMap[ad.start] = [];
        }

        // add items
        for (let ad of activityDates) {
          activityDateMap[ad.start].push(ad);
        }

        setActivityDateMap(activityDateMap);

        loadDateEvents(activityDates, meta);
        setBackDropOpen(false);

        cssDisabledDays(meta.excludedDates);
      },
    );
  };

  const cssDisabledDays = (disabledDates) => {
    for (let date of disabledDates) {
      let res = document.querySelectorAll(`[data-date="${date.date}"]`);
      if (res != null && res.length > 0) {
        res[0].style.backgroundColor = "#dfdfdf";
        let textRes = res[0].querySelectorAll(
          `[class="fc-daygrid-day-events"]`,
        );
        if (textRes != null && textRes.length > 0) {
          textRes[0].innerHTML = "&nbsp;&nbsp;" + date.name;
        }
      }
    }
  };

  const loadDateEvents = async (activityDates, meta) => {
    const events = [];

    if (isMobile === true) {
      // key: date, value: count
      const counts = {};
      // count up and display just the count
      for (let i = 0; i < activityDates.length; i++) {
        if (counts[activityDates[i].start]) {
          counts[activityDates[i].start] += 1;
        } else {
          counts[activityDates[i].start] = 1;
        }
      }

      for (const date in counts) {
        const event = {
          title: counts[date],
          start: date,
          backgroundColor: getTitleBackgroundColor(date, meta),
        };
        events.push(event);
      }
    } else {
      // show short label
      for (let i = 0; i < activityDates.length; i++) {
        const ad = activityDates[i];
        const periodNumber = ad.period;
        const event = {
          title:
            getPeriodSingleName(_school, periodNumber - 1) +
            ": " +
            truncate(ad.title, { length: 16 }),
          start: ad.start,
          backgroundColor: getTitleBackgroundColor(
            ad.start,
            meta,
            ad.categoryEid,
            ad.color,
          ),
          obj: ad,
        };
        events.push(event);
      }
    }

    setCalendarEvents(events);
  };

  const getTitleBackgroundColor = (
    yyyymmdd,
    meta,
    categoryEid = null,
    color = null,
  ) => {
    if (getUserPriority(_jwt) === 800 && categoryEid != null) {
      // use category colors
      return meta.categoryColors[categoryEid];
    }

    if (color != null) {
      return color;
    }

    if (
      meta != null &&
      meta.dayTypes != null &&
      meta.dayTypes[yyyymmdd] != null
    ) {
      const dayType = meta.dayTypes[yyyymmdd];

      switch (dayType) {
        case "A":
          return "#1976d2";
        case "B":
          return "#326d0e";
        case "C":
          return "#d72d79";
        case "D":
          return "#162e51";
        default:
          return "#1976d2";
      }
    }
    return "#1976d2";
  };

  const handleDateClick = (clickType, info) => {
    let dateStr = null;

    if (clickType === "date") {
      dateStr = info.dateStr;
    } else {
      // it's an event; stored somewhere else
      dateStr = dayjs(info.event.start).format("YYYY-MM-DD");
    }

    // otherTeacherEid

    if (
      otherTeacherEid != null &&
      (getUserPriority(_jwt) !== 800 || _calendarMeta.allowEditCalendar === "N")
    ) {
      handleRoDialog(dateStr);
    } else if (
      (_calendarMeta.allowEditCalendar === "Y" &&
        _calendarMeta.teacherEditEnabled === "Y") ||
      getUserPriority(_jwt) === 800
    ) {
      handleEditDialog(dateStr);
    } else {
      if (
        _calendarMeta.teacherEditEnabled === "N" &&
        getUserPriority(_jwt) < 800
      ) {
        showError(
          "Unable to Update Date.",
          "Your school has disabled the ability to edit the calendar. Please contact your School Administrator to enable.",
        );
        // } else if (_calendarMeta.allowEditCalendar === "N") {
        //   showError(
        //     "Unable to Update Date.",
        //     "Your school has disabled the ability to edit the calendar. Please contact your School Administrator to enable.",
        //   );
      }
    }
  };

  const getDisabledDate = (dateStr) => {
    return _disabledDaysMap[dateStr];
  };

  const isDisabledDate = (dateStr) => {
    // if specified date is disabled then disable
    return getDisabledDate(dateStr) != null;
  };

  // returns true if the teacher is permitted to add/manage a date
  const isDateOK = (dateStr) => {
    // const targetDate = dateStr;
    // const currentDate = dayjs().format("YYYY-MM-DD");

    // if (
    //   targetDate < currentDate &&
    //   isFaculty(_jwt) &&
    //   otherTeacherEid == null
    // ) {
    //   showError(
    //     "Unable to Update Activity Date",
    //     "Date has already passed by."
    //   );
    //   return false;
    // }

    // calculate cutoff hours
    // if (_calendarMeta.teacherCutoffHours > 0 && isFaculty(_jwt)) {
    //   const cutoffTime = dayjs(
    //     `${dateStr} ${_calendarMeta.activityCutoffHour}:${_calendarMeta.activityCutoffMinute}`
    //   )
    //     .subtract(_calendarMeta.teacherCutoffHours, "hour")
    //     .format("YYYYMMDDHHmm");

    //   const currentDateTime = dayjs().format("YYYYMMDDHHmm");

    //   if (cutoffTime < currentDateTime) {
    //     showError(
    //       "Unable to Update Activity Date",
    //       "Teacher Activity Cutoff Time has been exceeded.\nYou need to enter your daily activities within " +
    //         _calendarMeta.teacherCutoffHours +
    //         " hours of the student cutoff time (" +
    //         _calendarMeta.activityCutoffHour +
    //         ":" +
    //         _calendarMeta.activityCutoffMinute +
    //         ")."
    //     );
    //     return false;
    //   }
    // }

    return true;
  };

  const handleEditDialog = (dateStr) => {
    const mmddyyyy = dayjs(dateStr).format("MM/DD/YYYY");
    if (isDisabledDate(dateStr)) {
      showError(
        "Unable to change date.",
        "No Activities allowed for " + mmddyyyy + ".",
      );
      return;
    }
    if (!isDateOK(dateStr)) {
      return;
    }

    setSelectedActivityDateStr(dateStr);

    const map = clone(_selectedActivityMap);
    initDayDialog(dateStr, map);
    loadShowEditDayDialog();
    setSelectedActivityMap(map);
    mergeSetActivityLists(map);

    // setSelectedActivityList(map["p" + _selectedPeriod]);
  };

  const mergeSetActivityLists = (map) => {
    let activityList = [];
    for (let period = 1; period <= _school.maxActivityCount; period++) {
      activityList = activityList.concat(map["p" + period]);
    }
    setSelectedActivityList(activityList);
  };

  const handleRoDialog = (dateStr) => {
    const mmddyyyy = dayjs(dateStr).format("MM/DD/YYYY");
    if (isDisabledDate(dateStr)) {
      showError(
        "Unable to change date.",
        "No Activities allowed for " + mmddyyyy + ".",
      );
      return;
    }
    if (!isDateOK(dateStr)) {
      return;
    }

    setSelectedActivityDateStr(dateStr);
    const map = clone(_selectedActivityMap);

    initDayDialog(dateStr, map);
    loadShowRoDayDialog();
    setSelectedActivityMap(map);
    // setSelectedActivityList(map["p" + _selectedPeriod]);
    mergeSetActivityLists(map);
  };

  const clearSelectedActivities = (map) => {
    for (let i = 1; i <= 10; i++) {
      map[`p${i}`] = [];
    }
  };

  const initDayDialog = (dateStr, map) => {
    const oDate = dayjs(dateStr);
    setDialogTitleDetail(`${oDate.format("dddd")}, ${oDate.format("M/D")}`);

    // clear selected items
    // const dayActivityCount = _calendarMeta.activityCountsMap[dateStr];
    clearSelectedActivities(map);

    // add current days activities
    addExistingActivities(dateStr, map);
  };

  const addExistingActivities = (dateStr, map) => {
    const dayActivityList = _activityDateMap[dateStr];

    // bail if not existing activities
    if (!dayActivityList) {
      return;
    }

    for (let activity of dayActivityList) {
      // get current list
      const list = map["p" + activity.period];
      list.push(activity);

      // reset list object in map and apply
      map["p" + activity.period] = list;
    }
  };

  const loadShowEditDayDialog = () => {
    setSelectedPeriod(1);
    setEditDayDialogOpen(true);
  };

  const loadShowRoDayDialog = () => {
    setSelectedPeriod(1);
    setRoDayDialogOpen(true);
  };

  const handleEditDayDialogClose = () => {
    setEditDayDialogOpen(false);
  };

  const handleRoDayDialogClose = () => {
    setRoDayDialogOpen(false);
  };

  const handleChangePeriod = (event) => {
    const period = event.target.value;
    setSelectedPeriod(period);
  };

  const handleChangeDragPeriod = (event) => {
    const period = event.target.value;
    setSelectedDragPeriod(period);
  };

  const getHiddenDays = () => {
    let days = [0, 6];

    if (_school && _school.hiddenDays) {
      const arry = [];
      for (let i = 0; i < _school.hiddenDays.length; i++) {
        arry.push(parseInt(_school.hiddenDays[i]));
      }
      days = arry;
    }

    return days;
  };

  const handleRepeat = () => {
    if (!isEmpty(otherTeacherEid)) {
      navigate(`/teacher/activity/repeat/${otherTeacherEid}`);
    } else {
      navigate(`/teacher/activity/repeat`);
    }
  };

  const handleBulkChangeLocation = () => {
    navigate(`/teacher/activity/location`);
  };

  const handleActivityColors = () => {
    setColorSelectDialogOpen(true);
  };

  const handleBulkDelete = () => {
    navigate(`/teacher/activity/date`);
  };

  const getActivity = (eid) => {
    for (const activity of _activities) {
      if (activity.eid === eid) {
        return activity;
      }
    }
    return null;
  };

  // different services reference the activity eid, by eid and activityEid. This function sorts that out and returns it
  const getActivityEid = (activity) => {
    if (activity.eid != null) {
      return activity.eid;
    }
    return activity.activityEid;
  };

  const handleChangeActivity = (event) => {
    setSelectedActivityEid(event.target.value);
    let activity = getActivity(event.target.value);

    if (activity == null) {
      return;
    }

    // clone activity to make some changes to it
    activity = clone(activity);

    // see if activity is already in the selected list, then bail
    const list = _selectedActivityMap["p" + _selectedPeriod];
    for (const a of list) {
      const itemEid = a.eid ? a.eid : a.activityEid;
      if (itemEid === event.target.value) {
        return;
      }
    }

    activity.period = _selectedPeriod;

    // add to map/list
    const map = clone(_selectedActivityMap);
    map[`p${_selectedPeriod}`].push(activity);
    setSelectedActivityMap(map);

    // re-init the ui to add the item
    // setSelectedActivityList(map["p" + _selectedPeriod]);
    mergeSetActivityLists(map);

    // reset selecte activity item
    setSelectedActivityEid("XX");
  };

  // returns the selected activities for the specified date
  const getDayValues = () => {
    const values = {};

    values["date"] = _selectedActivityDateStr;
    values["otherTeacherEid"] = otherTeacherEid;

    // count number of activity periods
    const dayActivityCount =
      _calendarMeta.activityCountsMap[_selectedActivityDateStr];
    if (dayActivityCount == null) {
      return;
    }

    // loop the actual activity count for the day, and stuff values in there
    for (let i = 1; i <= dayActivityCount; i++) {
      // list of activities for that period
      // just scrape out the eids
      const list = _selectedActivityMap["p" + i];
      const eidList = [];
      for (const item of list) {
        eidList.push(getActivityEid(item));
      }

      values["period" + i] = eidList;
    }

    return values;
  };

  const validateDayDates = (values) => {
    doService(validateTeacherDay(_school, _jwt, values), (results) => {
      // check values for override
      const data = results.data;
      let message = "";

      const dayActivityCount =
        _calendarMeta.activityCountsMap[_selectedActivityDateStr];

      for (let i = 1; i <= dayActivityCount; i++) {
        const idsKey = "p" + i + "Ids";
        const countsKey = "p" + i + "Counts";

        const pIds = data[idsKey];
        const pCounts = data[countsKey];

        for (let a = 0; a < pIds.length; a++) {
          const activityEid = pIds[a];
          // if limit met then append
          let activityMessage = "";
          // bail if lunch count for teachers > threshold
          if (
            pCounts[activityEid + "_name"].indexOf("Teacher Lunch") >= 0 &&
            pCounts[activityEid + "_lunchCount"] >=
              _calendarMeta.maxTeacherLunchCount
          ) {
            message =
              "* MAX Teacher Lunches already taken for Period " +
              pCounts[activityEid + "_period"] +
              " on " +
              values["date"] +
              ".";
            showError("Unable to Save Activities", message);
            return;
          }

          if (
            pCounts[activityEid + "_activity"].length >=
            pCounts[activityEid + "_maxCount"]
          ) {
            if (pCounts[activityEid + "_override"] === "N") {
              activityMessage +=
                "* MAX activities already assigned to the location for <b>" +
                pCounts[activityEid + "_name"] +
                "</b> for Period " +
                i +
                " AND CANNOT BE OVERRIDDEN.<br/>";

              if (!isStringEmpty(pCounts[activityEid + "_noOverrideMessage"])) {
                activityMessage +=
                  pCounts[activityEid + "_noOverrideMessage"] + "<br/>";
              }
            } else {
              activityMessage +=
                "* MAX activities already assigned to the location for <b>" +
                pCounts[activityEid + "_name"] +
                "</b> for Period " +
                i +
                ".<br/>";
            }

            activityMessage += `<ul style="margin-top:0px;margin-bottom:0px">`;
            for (
              let j = 0;
              j < pCounts[activityEid + "_activity"].length;
              j++
            ) {
              activityMessage +=
                "<li>" +
                pCounts[activityEid + "_activity"][j]["originalActivityName"] +
                "</li>";
            }
            activityMessage += "</ul>";
          }

          message += activityMessage;
        }
      }

      if (message.length > 0) {
        showWarning(message);
        return;
      }

      // save if not issues
      saveDay(values);
    });
  };

  const saveDay = (values) => {
    console.log("Saving Activity Dates - Please Wait...");
    doService(saveTeacherDay(_school, _jwt, values), (results) => {
      setEditDayDialogOpen(false);
      // reload calendar for current month
      initCalendarData();
      showToast("S", "Saved Activity Date(s)");
    });
  };

  const showWarning = (message) => {
    setWarningMessage(message);
    setWarningDialogOpen(true);
  };

  const handleDayDialogSave = () => {
    const values = getDayValues();

    if (values == null) {
      alert(
        "This date is not available on the calendar.  Please select another date.",
      );
      setEditDayDialogOpen(false);
      return;
    }
    validateDayDates(values);
  };

  const handleDayDialogConfirmSave = () => {
    const values = getDayValues();
    saveDay(values);
    setWarningDialogOpen(false);
  };

  const removeActivity = (activityToRemove) => {
    if (!window.confirm("Click OK to remove activity.")) {
      return;
    }

    // get current list
    const mapClone = clone(_selectedActivityMap);
    let listClone = clone(_selectedActivityMap["p" + activityToRemove.period]);

    // 2 for 1 - find and remove object based on criterion
    // remove and reset list
    remove(listClone, function (activity) {
      let activityEid1 = getActivityEid(activity);
      let activityEid2 = getActivityEid(activityToRemove);
      return activityEid1 === activityEid2;
    });

    // reset list object in map and apply
    mapClone["p" + activityToRemove.period] = listClone;
    setSelectedActivityMap(mapClone);
    // setSelectedActivityList(listClone);
    mergeSetActivityLists(mapClone);
  };

  const handleClickViewAttendance = (activity) => {
    navigate(`/teacher/ad/${activity.activityDateEid}/attendance/view`);
  };

  const handleClickTakeAttendance = (activity) => {
    navigate(`/teacher/ad/${activity.activityDateEid}/attendance/take`);
  };

  const handleClickActivityAttendees = (activity) => {
    doService(
      getPreviousAttendance(
        _school,
        _jwt,
        activity.activityEid,
        activity.start,
        activity.period,
      ),
      (results) => {
        setActivityAttendees(results.data.attendees);
        setAttendeeDialogOpen(true);
      },
    );
  };

  const handleAttendeeDialogClose = () => {
    setAttendeeDialogOpen(false);
  };

  const renderDayCellContent = (cell) => {
    if (_calendarMeta.dayTypes == null) {
      return cell.dayNumberText;
    }

    if (isMobile) {
      return cell.dayNumberText;
    }

    // desktop - show day number
    let text = cell.dayNumberText;

    const yyyymmdd = getYYYYMMDD__JsDate(cell.date);
    if (
      _calendarMeta.dayTypes[yyyymmdd] != null &&
      _calendarMeta.showDayType === "Y"
    ) {
      text += ` (${_calendarMeta.dayTypes[yyyymmdd]})`;
    }

    return text;
  };

  const onClickQr = (activity) => {
    navigate(`/teacher/ad/attend/${activity.activityDateEid}`);
  };

  const onClickTakeAttendance = (activity) => {
    navigate(`/teacher/ad/${activity.activityDateEid}/attendance/take`);
  };

  const onClickChangeLocation = (ad) => {
    setSelectedActivityDate(ad);
    setLocationEid(ad.locationEid);
    setLocationDialogOpen(true);
  };

  const onClickUpdateLocation = () => {
    setBackDropOpen(true);
    setLocationDialogOpen(false);
    doService(
      changeActivityDateLocation(
        _school,
        _jwt,
        _selectedActivityDate.activityDateEid,
        _locationEid,
      ),
      (results) => {
        setBackDropOpen(false);
        initTeacherCalendar();
        showToast("S", "Location Updated!");
      },
    );
  };

  const onSaveActivityColor = (hexColor) => {
    doService(
      saveActivityColor(_school, _jwt, _selectedColorActivity, hexColor),
      (result) => {
        showToast("S", "Color Saved");
      },
    );
  };

  const hasActivityOnDate = (dateStr, period, activityEid) => {
    console.log("activityEid", activityEid);
    for (const event of _calendarEvents) {
      console.log(event);
      if (
        event.start === dateStr &&
        event.obj != null &&
        event.obj.period === period &&
        event.obj.activityEid === activityEid
      ) {
        return true;
      }
    }

    return false;
  };

  const handleActivityDrop = (activity, dateStr, period, info) => {
    console.log("Activity dropped on calendar", {
      activity,
      dateStr,
      period,
      info,
    });

    if (isDisabledDate(dateStr)) {
      // bail on disabled dates
      return;
    }

    if (hasActivityOnDate(dateStr, period, activity.eid)) {
      // if activity already assiged to that date/time then ignore;
      showInfo(
        `"${activity.name}" is already assigned on ${dayjs(dateStr).format("MM/DD")} for ${getPeriodName(_school, period)}.`,
      );
      return;
    }

    const dates = [];
    dates.push({
      activityEid: activity.eid,
      period: period,
      locationEid: activity.locationEid,
      date: dayjs(dateStr).format("YYYY-MM-DD"),
    });

    setShowProgress(true);
    console.log(dates);
    doService(adminBulkAddActivityDates(_school, _jwt, dates), (results) => {
      showToast("S", "Activity Added to Date!");
      setShowProgress(false);
      initCalendarData();
    });
  };

  const handleCalendarDrop = (info) => {
    const activityEid = info.draggedEl.getAttribute("data-activity-eid");
    const activity = getActivity(activityEid);

    handleActivityDrop(activity, info.dateStr, _selectedDragPeriod, info);
  };

  const handleEventContextMenu = (info, domEvent = null) => {
    const eventObj = info?.event?.extendedProps?.obj;
    if (eventObj == null) {
      return;
    }

    const clientX = domEvent?.clientX ?? 0;
    const clientY = domEvent?.clientY ?? 0;

    setCalendarContextActivity(eventObj);
    setCalendarContextMenu({
      mouseX: clientX + 2,
      mouseY: clientY + 2,
    });
  };

  const handleContextMenuClose = () => {
    setCalendarContextMenu(null);
    setCalendarContextActivity(null);
  };

  const handleContextMenuTakeAttendance = () => {
    if (_calendarContextActivity == null) {
      handleContextMenuClose();
      return;
    }

    const activityDateEid =
      _calendarContextActivity.activityDateEid ?? _calendarContextActivity.eid;
    if (activityDateEid == null) {
      handleContextMenuClose();
      return;
    }

    navigate(`/teacher/ad/${activityDateEid}/attendance/take`);
  };

  const handleContextMenuViewAttendance = () => {
    if (_calendarContextActivity == null) {
      handleContextMenuClose();
      return;
    }

    const activityDateEid =
      _calendarContextActivity.activityDateEid ?? _calendarContextActivity.eid;
    if (activityDateEid == null) {
      handleContextMenuClose();
      return;
    }

    navigate(`/teacher/ad/${activityDateEid}/attendance/view`);
  };

  const handleContextMenuRemove = () => {
    if (_calendarContextActivity == null) {
      handleContextMenuClose();
      return;
    }

    const activityDateEid =
      _calendarContextActivity.activityDateEid ?? _calendarContextActivity.eid;
    if (activityDateEid == null) {
      handleContextMenuClose();
      return;
    }

    handleContextMenuClose();
    setBackDropOpen(true);
    doService(
      deleteActivityDates(_school, _jwt, [activityDateEid]),
      (result) => {
        setBackDropOpen(false);
        initCalendarData();
        showToast("S", "Removed from Calendar");
      },
      null,
      null,
      () => {
        setBackDropOpen(false);
        handleContextMenuClose();
      },
    );
  };

  return (
    <>
      <DrawerAppBar />

      <Container>
        {isStringEmpty(otherTeacherEid) &&
          _school.activitiesEnabled === "Y" && (
            <>
              <Title text="Teacher Calendar" showBack={true} />
              <ProgressBar showProgress={_showProgress} />
              <VideoAlert
                text="How to Schedule an Activity"
                url="https://videos.alltimely.com/teacher-calendar.mp4"
              />

              {isMobile === true && (
                <Button
                  sx={{ mt: 1, mb: 1 }}
                  variant="contained"
                  fullWidth
                  onClick={handleRepeat}
                >
                  Repeat
                </Button>
              )}

              {isMobile === false && (
                <Grid container spacing={1} sx={{ pt: 1, pb: 1 }}>
                  <Grid
                    sx={{ pt: "0.5em !important", mb: 1 }}
                    item
                    xs={3}
                    md={3}
                  >
                    <Button
                      sx={{ mt: 0 }}
                      variant="contained"
                      fullWidth
                      onClick={handleRepeat}
                    >
                      Repeat
                    </Button>
                  </Grid>
                  <Grid sx={{ pt: "0.5em !important" }} item xs={3} md={3}>
                    <Button
                      fullWidth
                      sx={{ mt: 0 }}
                      variant="outlined"
                      className="button1"
                      onClick={handleBulkChangeLocation}
                    >
                      Bulk Change Location
                    </Button>
                  </Grid>
                  <Grid sx={{ pt: "0.5em !important" }} item xs={3} md={3}>
                    <Button
                      fullWidth
                      sx={{ mt: 0 }}
                      variant="outlined"
                      className="button2"
                      onClick={handleActivityColors}
                    >
                      Activity Colors
                    </Button>
                  </Grid>
                  <Grid sx={{ pt: "0.5em !important" }} item xs={3} md={3}>
                    <Button
                      fullWidth
                      sx={{ mt: 0 }}
                      variant="outlined"
                      onClick={handleBulkDelete}
                    >
                      Bulk Delete
                    </Button>
                  </Grid>
                </Grid>
              )}
            </>
          )}

        {!isStringEmpty(otherTeacherEid) && (
          <>
            <Title
              text={`Teacher Calendar ${
                _calendarMeta.teacher.first_name != null
                  ? "for " +
                    _calendarMeta.teacher.first_name +
                    " " +
                    _calendarMeta.teacher.last_name
                  : ""
              }`}
              showBack={true}
            />
            <Button
              sx={{ mt: 1, mb: 1 }}
              variant="contained"
              onClick={handleRepeat}
            >
              Repeat
            </Button>
          </>
        )}

        <br />

        <Grid container spacing={1} sx={{ pt: 1, pb: 1 }}>
          {isMobile === false && (
            <Grid item sx={{ width: "270px" }}>
              <Alert severity="info" sx={{ mb: 1 }} icon={false}>
                NEW: Select the period and then drag the activity to the
                calendar.
              </Alert>
              <Paper
                ref={draggableActivitiesRef}
                className="teacher-calendar-activity-palette"
                sx={{ mt: 1, mb: 1, maxHeight: "570px" }}
              >
                <div className="teacher-calendar-palette-header">
                  1. Select Period:
                </div>
                <div className="teacher-calendar-palette-control">
                  <Select
                    fullWidth
                    size="small"
                    onChange={handleChangeDragPeriod}
                    value={_selectedDragPeriod}
                  >
                    {[...Array(_school.maxActivityCount)].map((x, i) => (
                      <MenuItem key={i + 1} value={i + 1}>
                        {getPeriodName(_school, i)}
                      </MenuItem>
                    ))}
                  </Select>
                </div>
                <div className="teacher-calendar-palette-header">
                  2. Drag to Calendar:
                </div>
                <div className="teacher-calendar-activity-list">
                  {_activities.length === 0 && (
                    <div className="teacher-calendar-empty-activity">
                      No Activities Found
                    </div>
                  )}
                  {_activities.map((activity) => (
                    <div
                      key={activity.eid}
                      className="teacher-calendar-draggable-activity"
                      data-activity-eid={activity.eid}
                      data-activity-name={activity.name}
                    >
                      {activity.name}
                    </div>
                  ))}
                </div>
              </Paper>
            </Grid>
          )}
          <Grid item xs={isMobile === false ? 9 : 12}>
            <FullCalendar
              ref={calendarRef}
              initialView="dayGridMonth"
              locale="en"
              dateClick={(info) => handleDateClick("date", info)}
              eventClick={(info) => handleDateClick("event", info)}
              eventDidMount={(info) => {
                info.el.addEventListener("contextmenu", (event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  handleEventContextMenu(info, event);
                });
              }}
              customButtons={{
                nextButton: {
                  icon: "chevron-right",
                  click: function () {
                    const calendarApi = calendarRef.current.getApi();
                    calendarApi.next();
                    // force css to render for that month
                    cssDisabledDays(_calendarMeta.excludedDates);
                  },
                },
                previousButton: {
                  icon: "chevron-left",
                  click: function () {
                    const calendarApi = calendarRef.current.getApi();
                    calendarApi.prev();
                    // force css to render for that month
                    cssDisabledDays(_calendarMeta.excludedDates);
                  },
                },
              }}
              headerToolbar={{
                start: "title",
                end: "previousButton,nextButton",
                center: "dayGridMonth,timeGridWeek,timeGridDay",
              }}
              titleFormat={
                isMobile
                  ? { year: "numeric", month: "numeric" }
                  : { year: "numeric", month: "long" }
              }
              aspectRatio={_calendarRatio}
              hiddenDays={getHiddenDays()}
              plugins={[interactionPlugin, dayGridPlugin, timeGridPlugin]}
              events={_calendarEvents}
              droppable={true}
              drop={handleCalendarDrop}
              themeSystem="standard"
              dayCellContent={renderDayCellContent}
            />
          </Grid>
        </Grid>

        <br />
        <SubTitle text="Activity List" />

        <TableContainer component={Paper} sx={{ mt: 1, mb: 1 }}>
          <Table size="small">
            <TableHead>
              <StripedTableRow>
                <StripedTableCell align="center" width={80}>
                  Date
                </StripedTableCell>
                <StripedTableCell>Period/Activity</StripedTableCell>
                <StripedTableCell>Location</StripedTableCell>
                <StripedTableCell align="center">Signup Count</StripedTableCell>
                <StripedTableCell align="center">
                  Max Attendance
                </StripedTableCell>
                <StripedTableCell align="center">
                  Seats Remaining
                </StripedTableCell>
              </StripedTableRow>
            </TableHead>
            <TableBody>
              <TableRowNoResults data={_activityDates} />
              {_activityDates.map((ad) => (
                <StripedTableRow>
                  <StripedTableCell align="center">
                    {dayjs(ad.start).format("MM/DD (dd)")}
                  </StripedTableCell>
                  <StripedTableCell>{ad.fullTitle}</StripedTableCell>
                  <StripedTableCell>
                    <Link
                      component="button"
                      onClick={() => onClickChangeLocation(ad)}
                    >
                      {ad.locationName}
                    </Link>
                  </StripedTableCell>
                  <StripedTableCell align="center">
                    {ad.adCount}
                  </StripedTableCell>
                  <StripedTableCell align="center">
                    {ad.maxAttendance}
                  </StripedTableCell>
                  <StripedTableCell align="center">
                    {Math.max(ad.maxAttendance - ad.adCount, 0)}
                  </StripedTableCell>
                </StripedTableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={_backDropOpen}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </Container>

      <Menu
        open={_calendarContextMenu != null}
        onClose={handleContextMenuClose}
        anchorReference="anchorPosition"
        anchorPosition={
          _calendarContextMenu != null
            ? {
                top: _calendarContextMenu.mouseY,
                left: _calendarContextMenu.mouseX,
              }
            : undefined
        }
      >
        <MenuItem onClick={handleContextMenuTakeAttendance}>
          Take Attendance
        </MenuItem>
        {_school.canViewViewAttendance === "Y" && (
          <MenuItem onClick={handleContextMenuViewAttendance}>
            View Attendance
          </MenuItem>
        )}
        <MenuItem onClick={handleContextMenuRemove}>
          Remove from Calendar
        </MenuItem>
      </Menu>

      <Dialog fullWidth={true} open={_editDayDialogOpen}>
        <DialogTitle sx={{ pl: 1, pb: 0, pt: 0.5 }}>
          Activities: {_dialogTitleDetail}
        </DialogTitle>
        <DialogContent sx={{ pb: 0 }}>
          <InputLabel>Select Period:</InputLabel>
          <Select
            sx={{ mb: 1, mt: 0, pt: 0 }}
            fullWidth
            size="small"
            onChange={handleChangePeriod}
            value={_selectedPeriod}
          >
            {[...Array(_school.maxActivityCount)].map((x, i) => (
              <MenuItem key={i + 1} value={i + 1}>
                {getPeriodName(_school, i)}
              </MenuItem>
            ))}
          </Select>
          {_school.activitiesEnabled === "Y" && (
            <>
              <InputLabel>Select Activity to Add:</InputLabel>
              <Select
                sx={{ mb: 1 }}
                fullWidth
                size="small"
                onChange={handleChangeActivity}
                value={_selectedActivityEid}
              >
                <MenuItem key="XX" value="XX">
                  -- SELECT --
                </MenuItem>
                {_activities.map((activity) => (
                  <MenuItem key={activity.eid} value={activity.eid}>
                    {activity.name}
                  </MenuItem>
                ))}
              </Select>
            </>
          )}

          <InputLabel>
            Selected Activities ({_selectedActivityList.length}):
          </InputLabel>

          {Array.from({ length: _school.maxActivityCount }, (_, i) => (
            <>
              {_selectedActivityMap[`p${i + 1}`].length > 0 && (
                <>
                  <TableContainer component={Paper} sx={{ mt: 1, mb: 1 }}>
                    <Table size="small">
                      <TableHead>
                        <StripedTableRow>
                          <StripedTableCell colSpan={2}>
                            {getPeriodName(_school, i)} (
                            {_selectedActivityMap[`p${i + 1}`].length})
                          </StripedTableCell>
                        </StripedTableRow>
                      </TableHead>
                      <TableBody>
                        {_selectedActivityMap[`p${i + 1}`].map(
                          (activity, i) => (
                            <StripedTableRow>
                              <StripedTableCell>
                                {activity.name ? activity.name : activity.title}
                                {activity.eid != null && (
                                  <>
                                    <br />
                                    <i>Click Save to Take Attendance</i>
                                  </>
                                )}
                              </StripedTableCell>
                              <StripedTableCell width={30}>
                                <ButtonGroup variant="outlined">
                                  {activity.eid == null && (
                                    <>
                                      <Tooltip title="Take Attendance">
                                        <IconButton
                                          aria-label="take attendance"
                                          title="take attendance"
                                          onClick={() =>
                                            handleClickTakeAttendance(activity)
                                          }
                                          sx={{ pl: 0, pr: 0.5, pt: 0, pb: 0 }}
                                        >
                                          <GradingIcon />
                                        </IconButton>
                                      </Tooltip>
                                      {activity.attendanceModify === "Y" &&
                                        _school.canViewViewAttendance ===
                                          "Y" && (
                                          <Tooltip title="View Attendance">
                                            <IconButton
                                              aria-label="view attendance"
                                              title="view attendance"
                                              onClick={() =>
                                                handleClickViewAttendance(
                                                  activity,
                                                )
                                              }
                                              sx={{
                                                pl: 0,
                                                pr: 0.5,
                                                pt: 0,
                                                pb: 0,
                                              }}
                                            >
                                              <VisibilityIcon />
                                            </IconButton>
                                          </Tooltip>
                                        )}
                                    </>
                                  )}
                                  <Tooltip title="Remove Activity">
                                    <IconButton
                                      aria-label="delete"
                                      title="delete"
                                      onClick={() => removeActivity(activity)}
                                      sx={{ pl: 0, pr: 0, pt: 0, pb: 0 }}
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  </Tooltip>
                                </ButtonGroup>
                              </StripedTableCell>
                            </StripedTableRow>
                          ),
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </>
          ))}

          {/* <InputLabel>
            Selected Activities ({_selectedActivityList.length}):
          </InputLabel> */}
          {/* <TableContainer component={Paper} sx={{ mt: 1, mb: 2 }}>
            <Table size="small">
              {_selectedActivityList.length === 0 && (
                <StripedTableRow>
                  <StripedTableCell colSpan={10}>
                    -- NONE SELECTED --
                  </StripedTableCell>
                </StripedTableRow>
              )}
              {_selectedActivityList.map((activity, i) => (
                <StripedTableRow>
                  <StripedTableCell>
                    <b>{getPeriodName(_school, activity.period - 1)}</b>
                    <br />
                    {activity.name ? activity.name : activity.title}
                  </StripedTableCell>
                  <StripedTableCell width={30}>
                    <ButtonGroup variant="outlined">
                      {activity.eid == null && (
                        <>
                          <IconButton
                            aria-label="take attendance"
                            title="take attendance"
                            onClick={() => handleClickTakeAttendance(activity)}
                            sx={{ pl: 0, pr: 0.5 }}
                          >
                            <GradingIcon />
                          </IconButton>
                          {activity.attendanceModify === "Y" && (
                            <IconButton
                              aria-label="view attendance"
                              title="view attendance"
                              onClick={() =>
                                handleClickViewAttendance(activity)
                              }
                              sx={{ pl: 0, pr: 0.5 }}
                            >
                              <VisibilityIcon />
                            </IconButton>
                          )}
                        </>
                      )}
                      <IconButton
                        aria-label="delete"
                        title="delete"
                        onClick={() => removeActivity(activity)}
                        sx={{ pl: 0, pr: 0 }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ButtonGroup>
                  </StripedTableCell>
                </StripedTableRow>
              ))}
            </Table>
          </TableContainer> */}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={handleEditDayDialogClose}>
            Close
          </Button>
          {_school.activitiesEnabled === "Y" && (
            <>
              <Button variant="contained" onClick={handleDayDialogSave}>
                Save
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Dialog fullWidth={true} open={_warningDialogOpen}>
        <DialogTitle sx={{ p: 1 }}>
          WARNING - Confirm Activity Location Override
        </DialogTitle>
        <DialogContent sx={{ pb: 0 }}>
          <Alert severity="warning">
            The following activities exceed the maximum number of activities for
            their location. Click "CONFIRM AND SAVE" to override and continue.
          </Alert>
          <div dangerouslySetInnerHTML={{ __html: _warningMessage }} />
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => setWarningDialogOpen(false)}
          >
            Close
          </Button>
          <Button variant="contained" onClick={handleDayDialogConfirmSave}>
            CONFIRM AND Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog fullWidth={true} open={_roDayDialogOpen}>
        <DialogTitle sx={{ p: 1 }}>
          Activities: {_dialogTitleDetail}
        </DialogTitle>
        <DialogContent sx={{ pb: 0 }}>
          <TableContainer component={Paper} sx={{ mt: 1, mb: 2 }}>
            <Table size="small">
              <TableHead>
                <StripedTableRow>
                  <StripedTableCell>Period</StripedTableCell>
                  <StripedTableCell>Name</StripedTableCell>
                  <StripedTableCell>Location</StripedTableCell>
                  <StripedTableCell align="center">Actions</StripedTableCell>
                </StripedTableRow>
              </TableHead>
              <TableBody>
                {Array.from(Array(_school.maxActivityCount), (e, i) => {
                  return (
                    <>
                      {_selectedActivityMap["p" + (i + 1)].map((activity) => (
                        <StripedTableRow>
                          <StripedTableCell>
                            {getPeriodName(_school, i)}
                          </StripedTableCell>
                          <StripedTableCell>
                            <Link
                              className="link-pointer"
                              onClick={() => {
                                handleClickActivityAttendees(activity);
                              }}
                            >
                              {activity.title}
                            </Link>
                          </StripedTableCell>
                          <StripedTableCell>
                            {activity.locationName}
                          </StripedTableCell>
                          <StripedTableCell align="center">
                            <Link
                              className="link-pointer"
                              onClick={() => {
                                onClickQr(activity);
                              }}
                            >
                              QR
                            </Link>{" "}
                            |{" "}
                            <Link
                              className="link-pointer"
                              onClick={() => {
                                onClickTakeAttendance(activity);
                              }}
                            >
                              Take Attendance
                            </Link>
                          </StripedTableCell>
                        </StripedTableRow>
                      ))}
                    </>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={handleRoDayDialogClose}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog fullScreen={true} open={_attendeeDialogOpen}>
        <DialogTitle sx={{ p: 1 }}>Attendees for Activity</DialogTitle>
        <DialogContent sx={{ pb: 0 }}>
          <TableContainer component={Paper} sx={{ mt: 1, mb: 2 }}>
            <Table size="small">
              <TableHead>
                <StripedTableRow>
                  <StripedTableCell>Name</StripedTableCell>
                  <StripedTableCell>Attended</StripedTableCell>
                  <StripedTableCell>Late</StripedTableCell>
                </StripedTableRow>
              </TableHead>
              <TableBody>
                <TableRowNoResults data={_activityAttendees} />
                {_activityAttendees.map((att) => (
                  <StripedTableRow>
                    <StripedTableCell>
                      {att.last_name}, {att.first_name}
                    </StripedTableCell>
                    <StripedTableCell>{att.attended__str}</StripedTableCell>
                    <StripedTableCell>{att.isLate__str}</StripedTableCell>
                  </StripedTableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={handleAttendeeDialogClose}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        PaperProps={{
          sx: {
            width: 400,
            maxHeight: 270,
          },
        }}
        onClose={() => setLocationDialogOpen(false)}
        open={_locationDialogOpen}
      >
        <DialogTitle>Update Location</DialogTitle>

        <DialogContent>
          <InputLabel>Select Location:</InputLabel>
          <Select
            fullWidth
            onChange={(evt) => setLocationEid(evt.target.value)}
            value={_locationEid}
          >
            {_locations.map((location) => (
              <MenuItem key={location.eid} value={location.eid}>
                {location.name}
              </MenuItem>
            ))}
          </Select>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => setLocationDialogOpen(false)}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={onClickUpdateLocation}>
            Update Location
          </Button>
        </DialogActions>
      </Dialog>

      <ColorSelectDialog
        open={_colorSelectDialogOpen}
        colors={COLORS}
        selectedColor={_color}
        activities={_activities}
        onClose={() => setColorSelectDialogOpen(false)}
        onSelectColor={(c) => {
          // setColor(c);
          setColorSelectDialogOpen(false);
          onSaveActivityColor(c);
        }}
        onSelectColorActivity={(evt) => {
          setSelectedColorActivity(evt.target.value);
        }}
        selectedColorActivity={_selectedColorActivity}
      />
    </>
  );
};

export default TeacherCalendar;
